// Import necessary React hooks
import { useState } from "react";
// Import custom components, functions and hooks
import {
  useAuthInfo,
  useServerSidePagination,
  ServerDataGridTable,
  buildCsvColumns,
  downloadCsvViaExport,
} from "../../common";
import { allItemsColumnConfig, AllItemsItemDetailsDialog } from "./itemDetails";

// Resolves the paginated items endpoint (after `${apiURI}/form/`) for the user's
// role. Procurement and sample-control roles see every request's items; the
// approver roles are scoped to the requests that concern them.
function getItemsEndpoint(decoded) {
  switch (decoded?.role) {
    case "procurement":
    case "sampleControl":
    case "sampleControlView":
      return "getItemsAll";
    case "chef":
      return `getItemsEmail/${decoded.email}`;
    case "executiveChef":
      return `getItemsExecutiveChef/${decoded.email}`;
    case "hod":
      return `getItemsHOD/${decoded.department}`;
    default:
      return undefined;
  }
}

/**
 * AllItems component for displaying all individual items in a server-paginated,
 * server-searched table. Each row is one item; clicking a row opens its details
 * dialog.
 */
export function AllItems() {
  // Retrieve decoded authentication information
  const decoded = useAuthInfo();
  // Server-side pagination + per-column search for the role-appropriate endpoint
  const {
    rows,
    rowCount,
    paginationModel,
    setPaginationModel,
    searchModel,
    onSearchChange,
    onClearAllSearch,
    activeFilterCount,
    filterResetKey,
    sortModel,
    onSortChange,
  } = useServerSidePagination(getItemsEndpoint(decoded));

  // Item details modal state
  const [rowData, setRowData] = useState();
  const [openItemDetailsModal, setOpenItemDetailsModal] = useState(false);
  const handleRowClick = (event) => {
    setRowData(event.row);
    setOpenItemDetailsModal(true);
  };

  return (
    <span>
      <ServerDataGridTable
        title="Individual Items List"
        description="Click on a row to view the corresponding item's details"
        columnConfig={allItemsColumnConfig}
        enableSearch
        rows={rows}
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        // Each flattened row is unique per (request, item).
        getRowId={(row) => `${row.sampleRequestID}__${row.sampleItemID}`}
        onRowClick={handleRowClick}
        searchModel={searchModel}
        onSearchChange={onSearchChange}
        onClearAllSearch={onClearAllSearch}
        activeFilterCount={activeFilterCount}
        filterResetKey={filterResetKey}
        sortModel={sortModel}
        onSortChange={onSortChange}
        onExport={() =>
          downloadCsvViaExport({
            list: "items",
            columns: buildCsvColumns(allItemsColumnConfig),
            search: searchModel,
            sort: sortModel.length
              ? { field: sortModel[0].field, dir: sortModel[0].sort }
              : undefined,
            filename: `all-items_${new Date().toISOString().slice(0, 10)}.csv`,
          })
        }
        columnGrouping
        columnGroupingModel={[
          {
            groupId: "Procurement Approval",
            description: "",
            children: [{ field: "status" }, { field: "processedBy" }],
          },
        ]}
      />

      {rowData && (
        <AllItemsItemDetailsDialog
          rowData={rowData}
          open={openItemDetailsModal}
          handleClose={() => setOpenItemDetailsModal(false)}
        />
      )}
    </span>
  );
}
